import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ================= IMAGE HELPER ================= */
const getBase64FromUrl = async (url) => {
  if (!url || url === "N/A" || url === "null" || url === "undefined") {
    return null;
  }
  
  try {
    let res = await fetch(url);

    if (!res.ok) {
      const token = localStorage.getItem("token");
      if (token) {
        res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      }
    }

    if (!res.ok) {
      console.warn(`Failed to fetch image: ${res.status} ${res.statusText}`);
      return null;
    }

    const blob = await res.blob();
    
    if (!blob.type.startsWith("image/")) {
      console.warn(`Invalid image type: ${blob.type}`);
      return null;
    }

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => {
        console.warn("Failed to read image as data URL");
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Error fetching image:", error.message);
    return null;
  }
};

/* ================= MAIN EXPORTER ================= */
export const exportToPDF = async ({
  fileName,
  title,
  data,
  columns,
  generatedOnText,
  landscape = false,
  headFillColor = [246, 177, 177],
  headTextColor = [0, 0, 0],
}) => {
  if (!data || !data.length) return;

  /* ---------- PDF SETUP ---------- */
  const doc = new jsPDF(landscape ? "l" : "p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 15;

  /* ---------- AUTO SCALE COLUMN WIDTHS ---------- */
  const maxTableWidth = pageWidth - marginX * 2;

  const rawTableWidth = columns.reduce(
    (sum, col) => sum + (col.width || 20),
    0
  );

  if (rawTableWidth > maxTableWidth) {
    const scale = maxTableWidth / rawTableWidth;
    columns.forEach((col) => {
      if (col.width) {
        col.width = Math.max(10, Math.floor(col.width * scale));
      }
    });
  }

  /* ---------- AUTO CENTER TABLE (OPTION-1) ---------- */
  const tableWidth = columns.reduce(
    (sum, col) => sum + (col.width || 20),
    0
  );

  const startX =
    tableWidth + marginX * 2 < pageWidth
      ? (pageWidth - tableWidth) / 2
      : marginX;

  /* ---------- PROCESS IMAGE DATA ---------- */
  const processedData = await Promise.all(
    data.map(async (row) => {
      const newRow = { ...row };

      for (const col of columns) {
        if (col.isImage) {
          const value =
            typeof col.accessor === "function"
              ? col.accessor(row)
              : row[col.accessor];

          // Only process if value is a valid non-empty string
          const isValidString = typeof value === "string" && 
            value.trim().length > 0 && 
            !["N/A", "null", "undefined", ""].includes(value.trim());

          newRow[`__img_${col.header}`] =
            isValidString
              ? value.startsWith("data:image")
                ? value
                : await getBase64FromUrl(value)
              : null;
        }
      }
      return newRow;
    })
  );

  /* ---------- HEADER ---------- */
  if (generatedOnText) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(generatedOnText, pageWidth / 2, 15, { align: "center" });
  }

  /* ---------- TITLE ---------- */
  doc.setFontSize(14);
  doc.text(title, pageWidth / 2, 25, { align: "center" });

  /* ---------- TABLE DATA ---------- */
  const headers = columns.map((c) => c.header);

  const body = processedData.map((row, rowIndex) =>
    columns.map((col) => {
      if (col.isImage) return "";
      return typeof col.accessor === "function"
        ? col.accessor(row, rowIndex)
        : row[col.accessor];
    })
  );

  const columnStyles = {};
  columns.forEach((col, index) => {
    if (col.width) columnStyles[index] = { cellWidth: col.width };
  });

  const IMAGE_SIZE = 14;
  const IMAGE_PADDING = 4;

  /* ---------- TABLE ---------- */
  autoTable(doc, {
    startY: 40,
    startX, // ✅ AUTO-CENTERED
    head: [headers],
    body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: headFillColor,
      textColor: headTextColor,
      fontStyle: "bold",
    },
    columnStyles,

    /* ---- DYNAMIC ROW HEIGHT ---- */
    didParseCell(cell) {
      if (cell.section !== "body") return;

      const col = columns[cell.column.index];
      if (!col?.isImage) return;

      const img =
        processedData[cell.row.index]?.[`__img_${col.header}`];

      if (img) {
        cell.cell.styles.minCellHeight =
          IMAGE_SIZE + IMAGE_PADDING;
      }
    },

    /* ---- DRAW IMAGES (BODY ONLY) ---- */
    didDrawCell(cell) {
      if (cell.section !== "body") return;

      const col = columns[cell.column.index];
      if (!col?.isImage) return;

      const img =
        processedData[cell.row.index]?.[`__img_${col.header}`];
      if (!img) return;

      const x =
        cell.cell.x +
        (cell.cell.width - IMAGE_SIZE) / 2;
      const y =
        cell.cell.y +
        (cell.cell.height - IMAGE_SIZE) / 2;

      doc.addImage(
        img,
        "JPEG",
        x,
        y,
        IMAGE_SIZE,
        IMAGE_SIZE
      );
    },
  });

  doc.save(`${fileName}.pdf`);
};
